import { useQueries, useQuery } from '@tanstack/react-query';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import {
  getBoostMultipliersForNfts,
  getOwnedNfts,
  getStakedNfts,
} from '@ui/queries/nfts';
import type { NftCollection, NftInfo, StakedNftInfo } from '@ui/queries/types';
import {
  getNftCollectionImageUrl,
  getNftCollectionNftName,
  getNftCollections,
} from '@ui/queries/types';
import type { GardenType } from '@ui/utils/garden';
import { currentWallet } from '@veno-app/wallet';
import { useMemo, useState } from 'react';

import { useCoinMode } from './useCoinMode';

const { useAccount } = currentWallet;

const useNfts = (
  collection: NftCollection,
): {
  isFetched: boolean;
  isLoading: boolean;
  data: NftInfo[] | undefined;
} => {
  const sdk = useVenoSDK();
  const ownerAddress = useAccount();
  const [cursors, setCursors] = useState<string[]>([]);

  const queries = [
    getOwnedNfts(ownerAddress ?? '', collection, sdk),
    ...cursors.map((c) => getOwnedNfts(ownerAddress ?? '', collection, sdk, c)),
  ];

  const results = useQueries({
    queries: queries.map((q) => ({ ...q })),
  });

  const lastCursor = useMemo(
    () =>
      results.length === 0 ||
      results[results.length - 1].isLoading ||
      !results[results.length - 1].data?.pageInfo?.hasNextPage
        ? undefined
        : results[results.length - 1].data?.pageInfo.endCursor,
    [results],
  );

  if (lastCursor) {
    setCursors((prev) =>
      prev.includes(lastCursor) ? prev : [...prev, lastCursor],
    );
  }

  return {
    isFetched: results.find((r) => !r.isFetched) ? false : true,
    isLoading: results.find((r) => r.isLoading) ? true : false,
    data: results[0].isLoading
      ? undefined
      : (
          results
            .filter((r) => r.data)
            .map((r) => r.data?.assets)
            .flat() as {
            name: string;
            tokenId: string;
            image: {
              url: string;
            };
          }[]
        ).map((nft) => ({
          ...nft,
          imageUrl: nft.image.url,
          collection: collection,
          id: nft.tokenId,
          multiplier: undefined,
        })),
  };
};

const useOwnedNfts = () => {
  const sdk = useVenoSDK();
  const ownedNfts: NftInfo[] = [];
  const nftCollections = getNftCollections(sdk.chainType);

  const NftsMap: Record<NftCollection, NftInfo[] | undefined> = {
    CRUISER: useNfts('CRUISER').data,
    MOLE: useNfts('MOLE').data,
    BoomerSquad: useNfts('BoomerSquad').data,
    Ballies: useNfts('Ballies').data,
    TopDogCorgi: useNfts('TopDogCorgi').data,
    CronosChimp: useNfts('CronosChimp').data,
  };

  const isLoading = Object.values(NftsMap).some(
    (v) => typeof v === 'undefined',
  );

  if (isLoading) {
    return undefined;
  }

  for (const collection of nftCollections) {
    const nfts = NftsMap[collection];
    if (nfts) {
      ownedNfts.push(...nfts);
    }
  }

  return ownedNfts;
};

const useNftImageMap = (
  nfts: { collection: NftCollection; id: string }[] | undefined,
) => {
  return useQuery(
    ['useNftImages', nfts],
    async () => {
      const imageMap: Record<NftCollection, Record<string, string>> = {
        Ballies: {},
        BoomerSquad: {},
        CRUISER: {},
        MOLE: {},
        TopDogCorgi: {},
        CronosChimp: {},
      };

      await Promise.allSettled(
        (nfts ?? []).map(async (nft) => {
          imageMap[nft.collection][nft.id] = '';
          const url = await getNftCollectionImageUrl(nft.collection, nft.id);
          imageMap[nft.collection][nft.id] = url;
        }),
      );

      return imageMap;
    },
    {
      enabled: Boolean(nfts),
    },
  );
};

export const useNftsWithMultipliers = (): NftInfo[] | undefined => {
  const sdk = useVenoSDK();
  const ownerAddress = useAccount();
  const ownedNfts = useOwnedNfts();
  const imageUrlMap = useNftImageMap(ownedNfts).data;

  const multipliersQuery = getBoostMultipliersForNfts(ownedNfts ?? [], sdk);
  const { data: multipliers } = useQuery({
    ...multipliersQuery,
    enabled: !!ownerAddress && !!ownedNfts?.length,
  });

  if (!ownedNfts || (ownedNfts.length && !multipliers) || !imageUrlMap)
    return undefined;

  return ownedNfts
    .map((nft, i) => ({
      ...nft,
      imageUrl: imageUrlMap[nft.collection][nft.id],
      multiplier: multipliers ? multipliers[i] : undefined,
    }))
    .sort((a, b) => (b.multiplier ?? 0) - (a.multiplier ?? 0));
};

export const useNumberOfStakedNfts = (type: GardenType) => {
  const coinMode = useCoinMode();
  const sdk = useVenoSDK();
  const ownerAddress = useAccount();
  const isZksync = currentWallet.useIsZksyncChainId();
  const query = getStakedNfts(
    coinMode,
    type,
    ownerAddress ?? '',
    sdk,
    isZksync,
  );
  const { data: ids, isLoading } = useQuery({
    ...query,
    enabled: !!ownerAddress,
  });

  return { data: ids?.length, isLoading };
};

export const useStakedNfts = (
  type: GardenType,
): {
  data: StakedNftInfo[] | undefined;
  isLoading: boolean;
} => {
  const coinMode = useCoinMode();
  const sdk = useVenoSDK();
  const ownerAddress = useAccount();
  const isZksync = currentWallet.useIsZksyncChainId();
  const query = getStakedNfts(
    coinMode,
    type,
    ownerAddress ?? '',
    sdk,
    isZksync,
  );
  const { data: nfts, isLoading: idsLoading } = useQuery({
    ...query,
    enabled: !!ownerAddress,
    staleTime: 10_000,
  });

  const multipliersQuery = getBoostMultipliersForNfts(nfts ?? [], sdk);
  const { data: multipliers, isLoading: multipliersLoading } = useQuery({
    ...multipliersQuery,
    enabled: !!ownerAddress && !!nfts?.length,
  });

  const imageUrlMap = useNftImageMap(nfts).data;

  if (!imageUrlMap) return { data: undefined, isLoading: true };
  if (nfts && nfts.length === 0) return { data: [], isLoading: false };
  if (!nfts || multipliersLoading) {
    return { data: undefined, isLoading: multipliersLoading || idsLoading };
  }

  const cruisers = nfts.map((nft, i) => ({
    id: nft.id.toString(),
    tokenId: nft.id.toString(),
    collection: nft.collection,
    multiplier: multipliers ? multipliers[i] : 1.0,
    name: `${getNftCollectionNftName(nft.collection)} #${nft.id}`,
    image: {
      url: imageUrlMap[nft.collection][nft.id],
    },
    imageUrl: imageUrlMap[nft.collection][nft.id],
  }));

  return { data: cruisers, isLoading: false };
};

export default useNfts;

export const useHasNftHelperCollections = () => {
  const sdk = useVenoSDK();
  return getNftCollections(sdk.chainType).length > 0;
};
